/**
 * 10-second onboarding tour for first-time NECS admins.
 *
 * Shown automatically on the Home view when localStorage flag
 * `necs.admin.tour.shown.<user_id>` is absent. Auto-advances every 2.5s
 * through 4 steps; user can skip with "Got it". Marks completion in
 * localStorage so it never reappears for the same admin.
 *
 * Each step highlights a target element by `data-testid` — we draw a
 * soft gold ring around that element's bounding rect using a Portal,
 * and float a tooltip card just below it.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

const STEP_INTERVAL_MS = 2500; // 4 steps × 2.5s = ~10 seconds total

const STEPS = [
  {
    target: '[data-testid="home-welcome-strip"]',
    title: 'Welcome to NECS Admin',
    body: 'This is your Home — your name and personalized department info live here.',
    side: 'bottom',
  },
  {
    target: '[data-testid="company-manager-dashboard"] nav',
    title: 'Profile bar',
    body: 'Top right has your notifications, your avatar, and a quick sign-out.',
    side: 'bottom',
  },
  {
    target: '[data-testid="sidebar-department-section"]',
    title: 'Your sidebar',
    body: 'Switch between departments here. Locked items mean you don\'t have clearance.',
    side: 'right',
  },
  {
    target: '[data-testid="sidebar-link-operations"]',
    title: 'Operations · Command Center',
    body: 'Click Operations any time you need the live company-wide dashboard.',
    side: 'right',
  },
];

function getKey(user) {
  if (!user) return null;
  return `necs.admin.tour.shown.${user.id || user.email || user.badge_number}`;
}

export default function OnboardingTour({ user, onClose }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const step = STEPS[stepIdx];

  // Auto-advance
  useEffect(() => {
    const t = setTimeout(() => {
      if (stepIdx < STEPS.length - 1) {
        setStepIdx((i) => i + 1);
      } else {
        complete();
      }
    }, STEP_INTERVAL_MS);
    return () => clearTimeout(t);
  }, [stepIdx]);

  // Measure the highlighted target on each step
  useEffect(() => {
    const el = document.querySelector(step.target);
    if (!el) {
      setRect(null);
      return;
    }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [step]);

  function complete() {
    const key = getKey(user);
    if (key) {
      try { localStorage.setItem(key, '1'); } catch (_) { /* private mode */ }
    }
    onClose && onClose();
  }

  // Tooltip position
  const tooltipStyle = (() => {
    if (!rect) {
      return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    }
    if (step.side === 'right') {
      return { top: rect.top + 8, left: rect.left + rect.width + 16 };
    }
    return { top: rect.top + rect.height + 12, left: Math.max(16, rect.left) };
  })();

  return createPortal(
    <div className="fixed inset-0 z-[100]" data-testid="onboarding-tour" aria-live="polite">
      {/* Dim backdrop */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
        onClick={complete}
        data-testid="tour-backdrop"
      />

      {/* Highlighted ring around the target */}
      {rect && (
        <div
          className="absolute pointer-events-none transition-all duration-500 ease-out"
          style={{
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
            borderRadius: '14px',
            boxShadow: '0 0 0 3px #C9A962, 0 0 0 8px rgba(201,169,98,0.25), 0 0 40px rgba(201,169,98,0.55)',
          }}
          data-testid="tour-ring"
        />
      )}

      {/* Tooltip card */}
      <div
        className="absolute rounded-xl p-4 shadow-2xl transition-all duration-500 ease-out"
        style={{
          ...tooltipStyle,
          maxWidth: '320px',
          backgroundColor: '#1a1a1a',
          border: '1px solid #C9A962',
        }}
        data-testid={`tour-step-${stepIdx}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-2">
          <p
            className="text-[10px] uppercase tracking-widest"
            style={{ color: '#C9A962', letterSpacing: '0.2em' }}
          >
            Step {stepIdx + 1} of {STEPS.length}
          </p>
          <button
            type="button"
            onClick={complete}
            className="text-xs text-zinc-400 hover:text-white transition"
            data-testid="tour-skip"
          >
            Got it
          </button>
        </div>
        <h3
          className="text-base font-bold text-white mb-1"
          style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.04em' }}
        >
          {step.title.toUpperCase()}
        </h3>
        <p className="text-xs text-zinc-300 leading-relaxed">{step.body}</p>
        {/* Step pip indicator */}
        <div className="flex gap-1.5 mt-3">
          {STEPS.map((_, i) => (
            <span
              key={`pip-${i}`}
              className="h-1 flex-1 rounded-full transition-all"
              style={{ backgroundColor: i <= stepIdx ? '#C9A962' : 'rgba(255,255,255,0.18)' }}
            />
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}

/* Helper: should we show the tour for this user? */
export function shouldShowTour(user) {
  if (!user) return false;
  const key = getKey(user);
  if (!key) return false;
  try {
    return !localStorage.getItem(key);
  } catch (_) {
    return false;
  }
}
